import React, { useCallback, useMemo, useRef } from 'react';

import type { EmojiProvider } from '@atlaskit/emoji/types';
import EmojiResource from '@atlaskit/emoji/emoji-resource';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type {
	ExtensionParams,
	MultiBodiedExtensionActions,
} from '@atlaskit/editor-common/extensions/extension-handler';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { expVal } from '@confluence/feature-experiments';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import type { NativeTabsParameters } from '../nativeTabsTypes.ts';
import { useActiveTab } from '../hooks/useActiveTab.ts';
import { useHashNavigation } from '../hooks/useHashNavigation.ts';
import { useViewedAnalytics } from '../hooks/useViewedAnalytics.ts';
import { findAnchorMacroFrameIndexByHash } from '../utils/anchorMacroHash.ts';

import { NativeTabs } from './NativeTabs.tsx';
import { NativeTabsPdfRenderer } from './NativeTabsPdfRenderer.tsx';

export type TabsRendererProps = {
	node: ExtensionParams<NativeTabsParameters>;
	actions: MultiBodiedExtensionActions;
};

/**
 * MBE handler for Native Tabs in **read-only / renderer mode** (`canEdit: false`).
 * `node` / `actions` are injected by the renderer's MBE plugin (wired in T-9);
 * the manifest wrapper only mounts this component once `actions` is available.
 * Allows tab switching only.
 */
const TabsRendererContent = ({ node, actions }: TabsRendererProps): React.JSX.Element => {
	const renderServerPlaceholder = useIsSSROrHydrating();
	const { cloudId, userId } = useSessionData();
	const { activeIndex, handleTabClick } = useActiveTab(0);
	const rootRef = useRef<HTMLDivElement>(null);

	const emojiProvider = useMemo<Promise<EmojiProvider> | undefined>(
		() =>
			renderServerPlaceholder ? undefined : getEmojiProvider(EmojiResource, { cloudId, userId }),
		[renderServerPlaceholder, cloudId, userId],
	);

	const tabs = node.parameters?.tabs ?? [];

	const onTabClick = useCallback(
		(index: number) => {
			handleTabClick(index);
			actions.changeActive(index);
		},
		[actions, handleTabClick],
	);
	const resolveFrameIndexByHash = useCallback(
		(hashId: string) => findAnchorMacroFrameIndexByHash(node.content, hashId),
		[node.content],
	);

	useHashNavigation({
		tabs,
		activeIndex,
		onTabSelect: onTabClick,
		rootRef,
		resolveFrameIndexByHash,
	});

	useViewedAnalytics({
		mode: 'renderer',
	});

	const childrenContainer = actions.getChildrenContainer();

	if (
		PDF_EXPORT_PAGE.match(window.location.pathname) &&
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		fg('confluence_native_tabs_pdf_export_all_tabs')
	) {
		return (
			<NativeTabsPdfRenderer
				tabs={tabs}
				emojiProvider={emojiProvider}
				rootRef={rootRef}
				childrenContainer={childrenContainer}
			/>
		);
	}

	return (
		<NativeTabs
			tabs={tabs}
			activeIndex={activeIndex}
			onTabClick={onTabClick}
			emojiProvider={emojiProvider}
			rootRef={rootRef}
			canEdit={false}
			orientation={
				expVal('confluence_native_tabs_m2', 'isEnabled', false)
					? (node.parameters?.orientation ?? 'horizontal')
					: 'horizontal'
			}
			activeFrame={childrenContainer}
		/>
	);
};

export const TabsRenderer: React.FC<TabsRendererProps> = withErrorBoundary({
	attribution: Attribution.MIGRATIONS_TOOLING,
})(TabsRendererContent);

TabsRenderer.displayName = 'TabsRenderer';
